import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantDiningRoomService from '../../services/restaurant/RestaurantDiningRoomService';
import RestaurantTableService from '../../services/restaurant/RestaurantTableService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeDiningRoomModal,
  openDiningRoomCreate,
  openDiningRoomEdit,
  resetDiningRoomResults,
  setDiningRoomError,
  setDiningRoomLoading,
  setDiningRoomPage,
  setDiningRoomSaving,
  setDiningRoomUpdating,
  updateDiningRoomField,
} from '../../store/slices/restaurant/restaurantDiningRoomSlice';
import {
  RestaurantDiningRoomDTO,
  RestaurantDiningRoomFilters,
  RestaurantDiningRoomForm,
} from '../../types/restaurant/RestaurantDiningRoomTypes';
import {
  RestaurantTableDTO,
  RestaurantTableForm,
} from '../../types/restaurant/RestaurantTableTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantDiningRooms = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantDiningRoom);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);

  const load = async (filters: RestaurantDiningRoomFilters, silent = false) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    dispatch(setDiningRoomError(''));
    dispatch(silent ? setDiningRoomUpdating(true) : setDiningRoomLoading(true));
    try {
      const page = await RestaurantDiningRoomService.listFiltered(
        branchId,
        filters
      );
      if (currentRequest !== requestId.current) return;
      dispatch(setDiningRoomPage(page));
      return page;
    } catch (error: any) {
      if (currentRequest !== requestId.current) return;
      dispatch(
        setDiningRoomError(
          error?.response?.data?.message || 'No fue posible cargar los salones.'
        )
      );
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(
          silent ? setDiningRoomUpdating(false) : setDiningRoomLoading(false)
        );
      }
    }
  };

  const edit = async (room: RestaurantDiningRoomDTO) => {
    try {
      const response = await RestaurantDiningRoomService.get(room.id);
      dispatch(openDiningRoomEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const save = async (filters: RestaurantDiningRoomFilters) => {
    if (!branchId || state.saving) return;
    dispatch(setDiningRoomSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantDiningRoomService.update(state.form.id, state.form)
        : await RestaurantDiningRoomService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Salón guardado',
        response.message || 'El salón se guardó correctamente.'
      );
      dispatch(closeDiningRoomModal());
      await load(filters, true);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setDiningRoomSaving(false));
    }
  };

  const deactivate = (
    room: RestaurantDiningRoomDTO,
    filters: RestaurantDiningRoomFilters,
    onEmptyPage: () => void
  ) =>
    confirm({
      title: 'Desactivar salón',
      message: `¿Desea desactivar “${room.name}”? Ya no aparecerá en las operaciones activas.`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          const response = await RestaurantDiningRoomService.deactivate(
            room.id
          );
          show(
            response.type || 'success',
            response.title || 'Salón desactivado',
            response.message || 'El salón fue desactivado correctamente.'
          );
          const page = await load(filters, true);
          if (page?.empty && filters.page > 0) onEmptyPage();
        } catch (error) {
          handle(error);
        }
      },
    });

  /**
   * Alta rápida de mesa desde la pantalla de salones: crea una mesa asignada
   * al salón indicado con valores por defecto (libre, cuadrada). El código lo
   * genera el backend.
   */
  const createTableInline = async (input: {
    diningRoomId: number;
    name: string;
    capacity: number;
  }): Promise<RestaurantTableDTO | null> => {
    if (!branchId) return null;
    try {
      const form: RestaurantTableForm = {
        diningRoomId: input.diningRoomId,
        name: input.name.trim(),
        capacity: Number(input.capacity),
        status: 'FREE',
        shape: 'SQUARE',
        positionX: '',
        positionY: '',
        displayOrder: 1,
      };
      const response = await RestaurantTableService.create(branchId, form);
      show(
        response.type || 'success',
        response.title || 'Mesa creada',
        response.message || 'La mesa se creó correctamente.'
      );
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  const updateField = <K extends keyof RestaurantDiningRoomForm>(
    field: K,
    value: RestaurantDiningRoomForm[K]
  ) => dispatch(updateDiningRoomField({ field, value }));

  return {
    ...state,
    load,
    resetResults: () => {
      requestId.current += 1;
      dispatch(resetDiningRoomResults());
    },
    edit,
    save,
    deactivate,
    createTableInline,
    updateField,
    openCreate: () => dispatch(openDiningRoomCreate()),
    closeModal: () => dispatch(closeDiningRoomModal()),
  };
};

export default useRestaurantDiningRooms;
